import {
  alpha,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CSSProperties, PropsWithChildren } from "react";
import {
  FilterType,
  FilterValue,
  HeaderProps,
  SortByFn,
  TableInstance,
  useAsyncDebounce,
  useColumnOrder,
  useFilters,
  UseFiltersColumnProps,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { MainPaper } from "../surface/MainPaper";
import { DefaultColumnFilter } from "./filters/DefaultColumnFilter";
import { TableToolbar } from "./TableToolbar";
import { selectionHook } from "./hooks/SelectionHook";
import { TablePagination } from "./TablePagination";
import { ReactTableProps } from "./TableProps";
import { FilterChipBar } from "./FilterChipBar";
import { SmallScreenRowAction } from "./SmallScreenRowAction";
import EditableCell from "./columns/EditableCell";
import useEffectSkipInitialRender from "../../helpers/utils/useEffect";
import { filterDisplayHook } from "./hooks/useFilterExtender";
import { RowsPerPageOptions } from "../../helpers/constants/TableConfig";

const DefaultHeader: React.FC<HeaderProps<any>> = ({ column }) => (
  <>{column.id.startsWith("_") ? null : column.id}</>
);

// Replaces react-table's built-in "text" filter, whose autoRemove drops any falsy filter
// value and so silently discards a "false" selection on boolean columns.
const defaultTableFilter: FilterType<any> = (rows, columnIds, filterValue) =>
  rows.filter((row) =>
    columnIds.some((id) => {
      const rowValue = row.values[id];

      if (typeof rowValue === "boolean" || typeof filterValue === "boolean") {
        return rowValue === filterValue;
      }

      return String(rowValue)
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    })
  );

defaultTableFilter.autoRemove = (filterValue: FilterValue) =>
  filterValue === undefined || filterValue === null || filterValue === "";

const filterTypes = {
  default: defaultTableFilter,
};

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

// Replaces react-table's built-in "alphanumeric" sort, which coerces anything that is not a
// string or number to "" and so leaves boolean columns in their original order.
const defaultTableSort: SortByFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.values[columnId];
  const b = rowB.values[columnId];

  if (typeof a === "boolean" || typeof b === "boolean") {
    return Number(a === true) - Number(b === true);
  }

  if (typeof a === "number" && typeof b === "number") {
    return a === b ? 0 : a > b ? 1 : -1;
  }

  return collator.compare(String(a ?? ""), String(b ?? ""));
};

const sortTypes = {
  default: defaultTableSort,
};

const defaultColumn = {
  Filter: DefaultColumnFilter,
  Header: DefaultHeader,
  filter: "default",
  sortType: "default",
  // When using the useFlexLayout:
  minWidth: 30, // minWidth is only used as a limit for resizing
  width: 150, // width is used for both the flex-basis and flex-grow
  maxWidth: 200, // maxWidth is only used as a limit for resizing
};

const hooks = [
  useColumnOrder,
  useFilters,
  useSortBy,
  usePagination,
  useRowSelect,
  selectionHook,
  useFlexLayout,
  filterDisplayHook
];

export function ReactTable<T extends Record<string, unknown>>(
  props: PropsWithChildren<ReactTableProps<T>>
) {
  const {
    className,
    columns,
    isLoading,
    onAdd,
    onDelete,
    onEdit,
    onView,
    extraCommands,
    onPrint,
    inlineLock,
    inlineLockState = true,
    onInlineEdit,
    manualPagination,
    manualSortBy,
    manualFilters,
    pageChangeHandler,
    onExport,
    hideToolbar,
    initialState,
    initialFilters,
  } = props;

  const theme = useTheme();

  let derivedState = initialState !== undefined ? initialState : {pageIndex: 0, pageSize: RowsPerPageOptions[2], filters: initialFilters ?? []}

  const instance = useTable<T>(
    {
      ...props,
      columns,
      defaultColumn,
      initialState: derivedState,
      filterTypes,
      sortTypes,
      // When inline editing is enabled, editing a cell replaces the data array
      // reference, which would otherwise trigger react-table's auto-reset of
      // pagination, filters, and sorting. Disable those resets for inline-edit
      // tables so the current page, filters, and ordering are preserved.
      autoResetPage: !onInlineEdit,
      autoResetFilters: !onInlineEdit,
      autoResetSortBy: !onInlineEdit,
      autoResetSelectedRows: !onInlineEdit,
      autoResetGlobalFilter: !onInlineEdit,
    },
    ...hooks
  ); 

  const { getTableProps, headerGroups, getTableBodyProps, page, prepareRow } = instance;
  const {pageSize, pageIndex, filters, sortBy } = instance.state;

  const [delayFilter, setDelayFilter] = useState({value: undefined as boolean | undefined});

  useEffect(() => {
    if(manualFilters) {
      instance.delayFilterHandler=setDelayFilter
    }

  }, [manualFilters])

  const { ...tableProps } = getTableProps();
  const { ...tableBodyProps } = getTableBodyProps();

  const hideSelectionAndRowActions = !inlineLockState || (hideToolbar ?? false);

  const onFetchDataDebounced = useAsyncDebounce(pageChangeHandler ?? (() => {}), 100);

  //Call back to Table.tsx to retrieve data from server if page, page size, or sort by changes.
  //Changes to filters are handled in FilterChipBar.tsx when manualFilter is set to true and is only triggered
  //when filter modal is closed and there are filter changes.
  useEffectSkipInitialRender(() => {

    if (!!pageChangeHandler && (manualPagination || manualSortBy)) {
      onFetchDataDebounced({ currentPage: pageIndex, pageSize: pageSize, filters: filters, sortBy: sortBy})
    }

  }, [pageIndex, pageSize, sortBy]);

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <MainPaper className="table-bg">
      <Paper sx={{ boxShadow: "none" }} className="tableMainWrapper">
        <TableContainer>
          {hideToolbar !== true && (
            <>
              <TableToolbar 
                          instance={instance}
                          {...{
                              inlineLock,
                              inlineLockState,
                              // Pass other actions only if inlineLockState is false
                              ...(inlineLockState
                                  ? { onAdd,
                                    onEdit,
                                    onDelete,
                                    onView,
                                    extraCommands,
                                    onPrint,
                                    onExport} 
                                  : {                                 
                                  }),
                          }}
              />
              <FilterChipBar instance={instance} delayFilter={delayFilter.value} manualFilters={manualFilters} />
            </>
          )}

          <Table {...tableProps} size="small" className={className}>
            <TableHead>
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                          if (hideSelectionAndRowActions && column.id === "_selector") {
                              return null;
                          }
                    const style = {
                      textAlign: column.align ? column.align : "left ",
                      fontWeight: 700,
                      fontSize: ".92em",
                    } as CSSProperties;

                    const {
                      key: headerKey,
                      role: headerRole,
                      ...customHeaderProps
                    } = column.getHeaderProps();
                    const { title: sortTitle = "", ...columnSortByProps } =
                      column.getSortByToggleProps();

                    return (
                      <TableCell key={headerKey} {...customHeaderProps}
                        className={
                          (typeof headerKey !== "string" && "hide") || ""
                        }
                        scope={typeof headerKey !== "string" ? "Action" : headerKey !== "header_action" ? 'col' : headerKey }
                      >
                        {column.canSort ? (
                          <Tooltip title={sortTitle}>
                            <TableSortLabel
                              active={column.isSorted}
                              direction={column.isSortedDesc ? "desc" : "asc"}
                              {...columnSortByProps}
                              style={style}
                            >
                              {column.render("Header")}
                            </TableSortLabel>
                          </Tooltip>
                        ) : (
                          <div style={style}>{column.render("Header")}</div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>

            <TableBody {...tableBodyProps}>
              {page.map((row) => {
                prepareRow(row);
                const { ...rowProps } = row.getRowProps();

                return (
                  <TableRow
                    {...rowProps}
                    onClick={!inlineLockState ? undefined : () => row.toggleRowSelected()}
                    className={row.original.isValid === false ? "error-row" : ""}
                    sx={{
                      cursor: "pointer",
                      bgcolor: row.isSelected
                        ? alpha(theme.palette.primary.light, 0.1) : "inherit"
                    }}
                  >
                        {row.cells.map((cell, headerKey) => {
                            const { ...getCellProps } = cell.getCellProps();
                            const { Header: scope }: any = headerGroups[0]?.headers[headerKey];
                            const column = headerGroups[0]?.headers[headerKey];

                            // Determine if the column is editable
                            const InLineEdit = column.InLineEdit || false;
                            const inputType = column.inputType || 'text'; // Default to 'text' if not specified
                            const filterOptions = column.filterOptions;

                            if (hideSelectionAndRowActions && column.id === "_selector") {
                                return null;
                            }

                            return (
                                <TableCell
                                    {...getCellProps}
                                    sx={{
                                        fontSize: ".92em",
                                        textAlign: column.align ? column.align : "left ",
                                        justifyContent: column.align ? column.align : "left ",
                                        width: "100%",
                                        pr: column.align ? (column.align !== "left" ? "42px" : "16px") : "16px",

                                        //We only want to apply this logic if the table is actually using inline edit.
                                        //inlineLockState is not just used for inline editing. Also check if onInlineEdit is passed in.
                                        ...(!InLineEdit && !inlineLockState && onInlineEdit && {
                                            lineHeight: '4.1em',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }),
                                    }}
                                    className={
                                        (typeof column.Header !== "string" && "hide") || ""
                                    }
                                    scope={typeof column.Header !== "string" ? "Action" : column.Header}
                                >
                                    {InLineEdit && !inlineLockState ? (
                                        <EditableCell
                                            value={cell.value}
                                            columnId={cell.column.id}
                                            rowId={row.original.id}
                                            isEditable = {InLineEdit}
                                            inputType={inputType}
                                            filterOptions={filterOptions}
                                            onInlineEdit={onInlineEdit}
                                            instance={instance}
                                        />
                                    ) : (
                                        cell.render("Cell")
                                    )}
                                </TableCell>
                            );
                        })}
                    <SmallScreenRowAction<T> 
                      instance={instance}
                      row={row}
                            {...{ onAdd, onDelete, onEdit, onView, onPrint, extraCommands, inlineLock, onExport }}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination<T> instance={instance} />
        </TableContainer>
      </Paper>
    </MainPaper>
  );
}
